import { useEffect, useRef, useState } from 'react';

import { requestOutletFastPath } from '../services/connectionFlows';
import { OUTLET_MAPPING_STORAGE_PREFIX } from '../constants';

/**
 * Outlet fast path (NEA-5988): when this shop has no POS yet (a store added via
 * self-service expansion) — or its own multi-outlet source is credentialled but
 * unmapped — ask the backend whether the org's existing credentials can serve
 * the outlet list, skipping the re-authorisation entirely. A usable answer
 * feeds the same pendingOutletMapping state the post-OAuth picker uses; a
 * `needsReauth` answer surfaces as a hint that pre-selects the provider in the
 * standard connect flow.
 */
export const useOutletFastPath = (currentShop, stock, enabled) => {
	const attemptedForShopRef = useRef(null);
	const [fastPathReauth, setFastPathReauth] = useState(null);

	useEffect(() => {
		if (!enabled || !currentShop?.id) return;
		if (stock.pendingOutletMapping) return;

		// An OAuth return owns the picker. While the code is still being exchanged
		// (?code= in the URL) the source can already read credentialled-but-unmapped
		// from a refetch; and after the exchange, a remount can run this effect
		// before the provider's rehydration restores the persisted mapping. Both
		// windows would mint a duplicate connect session for the same outlet list.
		// Neither guard marks the shop as attempted, so a later eligible render
		// (e.g. a failed exchange that left no mapping) still gets the fast path.
		if (new URLSearchParams(window.location.search).has('code')) return;
		if (sessionStorage.getItem(OUTLET_MAPPING_STORAGE_PREFIX + currentShop.id)) return;

		const source = stock.source;
		const eligible = !source || (source.requiresLocationMapping && source.hasCredentials && !source.locationId);
		if (!eligible) return;

		if (attemptedForShopRef.current === currentShop.id) return;
		attemptedForShopRef.current = currentShop.id;

		let cancelled = false;
		(async () => {
			try {
				const result = await requestOutletFastPath(currentShop.id);
				if (cancelled || !result?.available) return;

				// Adopt only a mapping addressed to THIS shop — session.shop is the
				// credential sibling for the expansion cohort and must never become
				// the mapping target.
				if (result.locations?.length && result.sessionId && result.targetShopId === currentShop.id) {
					stock.adoptOutletMapping({
						sessionId: result.sessionId,
						shopId: result.targetShopId,
						locations: result.locations,
						providerLabel: result.providerLabel
					});
				} else if (result.needsReauth && result.provider) {
					setFastPathReauth({ provider: result.provider, providerLabel: result.providerLabel });
				}
			} catch (error) {
				// Best-effort — the standard connect flow is always available.
				console.error('Outlet fast-path lookup failed:', error);
			}
		})();

		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, currentShop?.id, stock.source, stock.pendingOutletMapping]);

	return { fastPathReauth };
};
